/**
 * Verwaltung: Benutzer des Prüfbüros, Einladungen, Betreiberkonten.
 *
 * Bewusst von `abfragen.ts` getrennt: das sind die Wege, die nur admin und
 * inhaber gehen. Sie liegen nie im Offlinespeicher – Zugänge vergibt man im
 * Büro, nicht auf dem Spielplatz.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'
import type { Rolle } from './typen'

export type Portalrolle = 'leitung' | 'mitarbeiter'

export const PORTALROLLENTEXT: Record<Portalrolle, string> = {
  leitung: 'Leitung',
  mitarbeiter: 'Mitarbeiter',
}

export type BenutzerZeile = {
  id: string
  email: string
  name: string
  rolle: Rolle
  aktiv: boolean
  letzte_anmeldung: string | null
  telefon: string | null
  zertifikat: string | null
  hat_passwort: boolean
  hat_microsoft: boolean
  /** Steht über den Mandanten. Nur zur Anzeige. */
  systemverwalter: boolean
  /** Leer heißt: sieht alle Kunden. */
  kunden_ids: string[]
}

export type EinladungZeile = {
  id: string
  email: string
  name: string
  rolle: Rolle
  laeuft_ab_am: string
  eingeloest_am: string | null
  erstellt_am: string
}

export type EinladungAus = EinladungZeile & { einladungslink: string | null }

export type BetreiberkontoZeile = {
  id: string
  kunde_id: string
  kunde_name: string
  email: string
  name: string
  rolle: Portalrolle
  aktiv: boolean
  letzte_anmeldung: string | null
  hat_passwort: boolean
}

export const verwaltungsschluessel = {
  benutzer: ['verwaltung', 'benutzer'] as const,
  einladungen: ['verwaltung', 'einladungen'] as const,
  betreiberkonten: (kundeId?: string) =>
    ['verwaltung', 'betreiberkonten', kundeId ?? 'alle'] as const,
}

// ── Benutzer ─────────────────────────────────────────────────────

export function useBenutzer(rest?: { enabled?: boolean }) {
  return useQuery({
    queryKey: verwaltungsschluessel.benutzer,
    queryFn: ({ signal }) => api.holen<BenutzerZeile[]>('/api/benutzer', signal),
    // Die Liste darf nur die Verwaltung lesen. Wer sie nicht darf, soll
    // keine Anfrage stellen, die mit 403 zurückkommt.
    enabled: rest?.enabled ?? true,
  })
}

export function useBenutzerAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Partial<BenutzerZeile>) =>
      api.aendern<BenutzerZeile>(`/api/benutzer/${id}`, daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: verwaltungsschluessel.benutzer }),
  })
}

export type RechtZeile = {
  recht: string
  text: string
  hat: boolean
  /** Bringt die Rolle dieses Recht mit? */
  aus_der_rolle: boolean
  /** Wurde es ausdrücklich erteilt oder entzogen? */
  abweichung: boolean
}

export type BenutzerRechte = {
  benutzer_id: string
  rolle: string
  /** Die Administration hat alles und lässt sich nichts entziehen. */
  unveraenderlich: boolean
  rechte: RechtZeile[]
}

export function useRechte(benutzerId: string) {
  return useQuery({
    queryKey: ['rechte', benutzerId] as const,
    queryFn: ({ signal }) =>
      api.holen<BenutzerRechte>(`/api/benutzer/${benutzerId}/rechte`, signal),
  })
}

export function useRechtSetzen(benutzerId: string) {
  const speicher = useQueryClient()
  return useMutation({
    // `gewaehrt: null` heißt zurücksetzen – dann gilt wieder die Rolle.
    mutationFn: (daten: { recht: string; gewaehrt: boolean | null }) =>
      api.setzen<BenutzerRechte>(`/api/benutzer/${benutzerId}/rechte`, daten),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: ['rechte', benutzerId] })
      // Betrifft es das eigene Konto, ändern sich die Knöpfe im Rahmen.
      void speicher.invalidateQueries({ queryKey: ['konto'] })
    },
  })
}

export function useEigenesAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      name?: string
      telefon?: string | null
      zertifikat?: string | null
      unterschrift?: string | null
      kalender_sync?: boolean
    }) =>
      api.aendern<BenutzerZeile>('/api/benutzer/ich', daten),
    onSuccess: () => {
      // Der Name steht in der Kopfzeile; er kommt aus /api/auth/ich.
      void speicher.invalidateQueries({ queryKey: verwaltungsschluessel.benutzer })
    },
  })
}

export function usePasswortAendern() {
  return useMutation({
    mutationFn: (daten: { altes_passwort: string; neues_passwort: string }) =>
      api.senden<void>('/api/auth/passwort', daten),
  })
}

// ── Einladungen ──────────────────────────────────────────────────

export function useEinladungen() {
  return useQuery({
    queryKey: verwaltungsschluessel.einladungen,
    queryFn: ({ signal }) => api.holen<EinladungZeile[]>('/api/einladungen', signal),
  })
}

export function useEinladungAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: { email: string; name: string; rolle: Rolle }) =>
      api.senden<EinladungAus>('/api/auth/einladungen', daten),
    onSuccess: () =>
      speicher.invalidateQueries({ queryKey: verwaltungsschluessel.einladungen }),
  })
}

export function useEinladungZuruecknehmen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/einladungen/${id}`),
    onSuccess: () =>
      speicher.invalidateQueries({ queryKey: verwaltungsschluessel.einladungen }),
  })
}

// ── Betreiberkonten ──────────────────────────────────────────────

export function useBetreiberkonten(kundeId?: string) {
  return useQuery({
    queryKey: verwaltungsschluessel.betreiberkonten(kundeId),
    queryFn: ({ signal }) =>
      api.holen<BetreiberkontoZeile[]>(
        kundeId ? `/api/betreiberkonten?kunde_id=${kundeId}` : '/api/betreiberkonten',
        signal,
      ),
  })
}

/** Nach jeder Änderung alle Kundenfilter neu laden, nicht nur den aktiven. */
function kontenNeuLaden(speicher: ReturnType<typeof useQueryClient>) {
  return speicher.invalidateQueries({ queryKey: ['verwaltung', 'betreiberkonten'] })
}

/**
 * Antwort nach dem Anlegen eines Kundenzugangs.
 *
 * `passwort` steht nur dann darin, wenn die Mail nicht hinausging — sonst
 * hat es der Empfänger schon, und es muss nicht zusätzlich über den
 * Bildschirm gehen. Ein zweites Mal anzeigen lässt es sich nie: gespeichert
 * ist nur der Hash.
 */
export type ZugangAngelegt = {
  konto: BetreiberkontoZeile
  passwort: string | null
  mail_verschickt: boolean
  hinweis: string | null
}

export function useBetreiberkontoAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      kunde_id: string
      email: string
      name: string
      rolle: Portalrolle
      passwort?: string
      passwort_erzeugen?: boolean
      zugangsdaten_senden?: boolean
      einmalpasswort?: boolean
    }) => api.senden<ZugangAngelegt>('/api/betreiberkonten', daten),
    onSuccess: () => kontenNeuLaden(speicher),
  })
}

export function useBetreiberkontoAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...daten
    }: {
      id: string
      name?: string
      rolle?: Portalrolle
      aktiv?: boolean
      passwort?: string
    }) => api.aendern<BetreiberkontoZeile>(`/api/betreiberkonten/${id}`, daten),
    onSuccess: () => kontenNeuLaden(speicher),
  })
}

export function useBetreiberkontoLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/betreiberkonten/${id}`),
    onSuccess: () => kontenNeuLaden(speicher),
  })
}

// ── Briefkopf ────────────────────────────────────────────────────

export type MandantZeile = {
  id: string
  name: string
  kurzform: string
  briefkopf: string | null
  zertifikat: string | null

  inhaber: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  land: string
  telefon: string | null
  email: string | null
  webseite: string | null

  steuernummer: string | null
  ust_id: string | null
  kleinunternehmer: boolean
  /** Als Text, damit beim Umweg über JavaScript keine Rundung entsteht. */
  steuersatz: string

  iban: string | null
  bic: string | null
  bank: string | null
  kontoinhaber: string | null

  zahlungsziel_tage: number
  fusstext: string | null
  glaeubiger_id: string | null

  /** Reichen die Angaben für eine Rechnung nach § 14 UStG? */
  rechnungsfaehig: boolean
  fehlende_pflichtangaben: string[]
}

export function useMandant() {
  return useQuery({
    queryKey: ['verwaltung', 'mandant'] as const,
    queryFn: ({ signal }) => api.holen<MandantZeile>('/api/mandant', signal),
    // Nur die Verwaltung darf das lesen. Für alle anderen antwortet der
    // Server mit 403 – das ist kein Fehler, sondern die Regel, und ein
    // Wiederholungsversuch würde nichts ändern.
    retry: false,
  })
}

export function useMandantAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Record<string, unknown>) =>
      api.aendern<MandantZeile>('/api/mandant', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['verwaltung', 'mandant'] }),
  })
}

// ── Microsoft 365 ────────────────────────────────────────────────

export type EinstellungStand = {
  gesetzt: boolean
  geheim: boolean
  /** "datenbank", "umgebung" oder "fehlt" – sagt, wo der Wert herkommt. */
  quelle: 'datenbank' | 'umgebung' | 'fehlt'
  lesbar: boolean
  /** Bei geheimen Feldern immer null. Sie gehen hinein, nicht heraus. */
  wert: string | null
  geaendert_am: string | null
}

export type MicrosoftStand = {
  felder: Record<string, EinstellungStand>
  anmeldung_moeglich: boolean
  versand_moeglich: boolean
  rueckkehr_url: string
}

export type ProbeErgebnis = { erfolg: boolean; meldung: string }

export function useMicrosoft() {
  return useQuery({
    queryKey: ['verwaltung', 'microsoft'] as const,
    queryFn: ({ signal }) => api.holen<MicrosoftStand>('/api/microsoft', signal),
  })
}

export function useMicrosoftSetzen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Record<string, string>) =>
      api.setzen<MicrosoftStand>('/api/microsoft', daten),
    onSuccess: () => speicher.invalidateQueries({ queryKey: ['verwaltung', 'microsoft'] }),
  })
}

export function useMicrosoftProbe() {
  return useMutation({
    mutationFn: () => api.senden<ProbeErgebnis>('/api/microsoft/probe'),
  })
}

export type Testanhang = 'kein' | 'klein' | 'gross'

export function useMicrosoftTestmail() {
  return useMutation({
    mutationFn: (anhang: Testanhang = 'kein') =>
      api.senden<ProbeErgebnis>(`/api/microsoft/testmail?anhang=${anhang}`),
  })
}
